import React, { useEffect, useState } from "react";
import axios from "axios";
import _ from "lodash";
import { get } from "lodash-es";
import { useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import { Modal } from "@mui/material";
import { useFetchEntityConfig } from "features/metadesk/redux/hooks";
import useNavigateEntity from "hooks/useNavigateEntity";
import { useTheme } from "@mui/styles";
import { apiUrl } from "features/configure";
import { useOutletContext } from "react-router";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import { openWindow } from "features/metadesk/utils";
import InputField from "@metacrm/metacrm-material-ui/dist/InputField";
import {
  Button,
  SIZE,
  COLOR,
  VARIANT,
} from "@metacrm/metacrm-material-ui/dist/Button";
import { validateURL } from "features/helpers/utils";

function ConnectingJira({ open }) {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  return (
    <Modal className="modal" open={open}>
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: 10,
          width: 350,
          textAlign: "center",
          padding: 48,
        }}>
        <img
          src={require(`assets/img/connecting-jira.svg`).default}
          alt="connecting jira"
        />
        <div
          style={{
            fontSize: 18,
            color: theme.customColors.grey[700],
            padding: 8,
            fontWeight: theme.fontWeight.BOLD,
          }}>
          {formatMessage({ id: "popup.connecting.title.jiraIssue" })}
        </div>
        <span style={{ fontSize: 14, color: theme.customColors.grey[600] }}>
          {formatMessage({ id: "popup.connecting.description" })}
        </span>
      </div>
    </Modal>
  );
}

function ConnectionFailedPopup({ open, onClose }) {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  return (
    <Modal className="modal" open={open}>
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: 10,
          width: 350,
          textAlign: "center",
          padding: "48px 48px 32px",
        }}>
        <div
          style={{
            fontSize: 18,
            color: theme.customColors.grey[700],
            fontWeight: theme.fontWeight.BOLD,
            marginBottom: 12,
          }}>
          {formatMessage({ id: "popup.connectionFailed.title" })}
        </div>
        <div
          style={{
            fontSize: 14,
            color: theme.customColors.grey[600],
            margin: "12px auto 32px",
          }}>
          {formatMessage({
            id: "popup.connectionFailed.description.jiraIssue",
          })}
        </div>
        <Button size={SIZE.LARGE} color={COLOR.SECONDARY} onClick={onClose}>
          {formatMessage({
            id: "popup.btn.tryAgain",
          })}
        </Button>
      </div>
    </Modal>
  );
}

const IntegrationInitJira = () => {
  const { navigateEntity } = useNavigateEntity();
  const { entityConfig } = useFetchEntityConfig();
  const [entityName, isCustomDomain] = useOutletContext();
  const dispatchSnackbar = useDispatchSnackbar();
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const [jiraUrl, setJiraUrl] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnectionFailed, setIsConnectionFailed] = useState(false);
  const isConnected = get(entityConfig, "serviceModule.jira.connected");

  const {
    handleSubmit,
    control,
    formState: { errors: errorsInfo, isValid },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: "",
  });

  useEffect(() => {
    if (isConnected) {
      navigateEntity("/settings/general/integration/update/jira");
    }
  }, [isConnected]);

  function navigateFallback() {
    navigateEntity("/settings/general/integration/add");
  }

  function tryAgain() {
    setIsConnectionFailed(false);
    setJiraUrl("");
    reset();
  }

  async function connectJira() {
    setIsConnecting(true);
    try {
      const { data } = await axios.post(
        `${apiUrl}/api/jira/${entityName}/save/workspaceUrl`,
        {
          workspaceUrl: jiraUrl,
        }
      );
      const url = data.redirectUrl;
      return openWindow(url, true);
    } catch (err) {
      dispatchSnackbar({ err });
      setIsConnectionFailed(true);
    }
    setIsConnecting(false);
  }

  return (
    <div style={{ width: "100%" }}>
      <ConnectingJira open={isConnecting} />
      <ConnectionFailedPopup open={isConnectionFailed} onClose={tryAgain} />
      <div>
        <Box
          sx={{
            color: "#A5A5A5",
            cursor: "pointer",
            fontWeight: 500,
            fontSize: 14,
            marginRight: 4,
            margin: "12px 0",
            marginTop: "-30px",
          }}
          onClick={() => {
            navigateEntity("/settings/general/integration/add");
          }}>
          Back
          <i
            className="meta-crm-icon-ic_redo font-size-14"
            style={{ marginLeft: 4 }}
          />
        </Box>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(90vh - 60px)",
          flexDirection: "column",
        }}>
        <Box
          fontWeight={theme.fontWeight.BOLD}
          fontSize={"24px"}
          color={theme.customColors.grey[800]}>
          {formatMessage({ id: "jiraIssueSettings.inputUrl.title" })}
        </Box>
        <Box
          sx={{
            padding: "8px 0px 32px",
            color: theme.customColors.grey[600],
            maxWidth: "574px",
            textAlign: "center",
          }}>
          {formatMessage({ id: "jiraIssueSettings.inputUrl.description" })}
        </Box>
        <div style={{ width: "100%", maxWidth: 579 }}>
          <InputField
            size={SIZE.XL}
            placeholder={formatMessage({
              id: "jiraIssueSettings.inputUrl.placeholder",
            })}
            name="url"
            control={control}
            isRequired={true}
            value={jiraUrl}
            onChange={(e) => {
              setJiraUrl(e.target.value);
            }}
            errors={_.get(errorsInfo, "url.message")}
            rules={{
              validate: (value) =>
                validateURL(value, { protocols: ["https"] }) ||
                formatMessage({
                  id: "global.inputCheck.invalidUrl",
                }),
            }}
          />
        </div>
        <div
          className="endRow"
          style={{ display: "flex", gap: 8, marginTop: 48 }}>
          <Button
            size={SIZE.SMALL}
            color={COLOR.SECONDARY}
            variant={VARIANT.OUTLINED}
            onClick={() => navigateFallback()}>
            {formatMessage({ id: "global.btn.cancel" })}
          </Button>
          <Button
            disabled={!isValid || !jiraUrl.length}
            size={SIZE.SMALL}
            color={COLOR.SECONDARY}
            onClick={() => connectJira()}>
            {formatMessage({ id: "jiraIssueSettings.inputUrl.submit" })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IntegrationInitJira;
